import { defineStore } from 'pinia'
import { useNodeStore } from './NodeStore.js'
import { usePodStore } from './PodStore.js'

import { formatDate } from '@/components/formatters.js';
import min from 'date-fns/min';
import max from 'date-fns/max';
// import Api from '../api/Api'

export const useFilterStore = defineStore('filter', {
  state: () => ({
    archivedFilter: false,
    // selectedNodeTypes: [],
    datesRange: [],
    selectedUploaders: [],
    selectedFileTypes: [],
    selectedPodStatuses: [],

    sortColumn: '',
    sortDesc: false,
  }),
  getters:{
    getSortColumn: function(state) {
      return state.sortColumn;
    },
    getSortDesc: function(state) {
      return state.sortDesc;
    },


    getNodes: function() {
      const nodeStore = useNodeStore();
      return nodeStore.getChildNodes;
    },
    getPods: function() {
      const podStore = usePodStore();
      return podStore.getPods.filter(x => x.parentpodid == null);
    },


    getArchivedFilter: function(state) {
      return state.archivedFilter;
    },
    getArchivedNodes: function(state) {
      return this.getNodes.filter(function(x) {
        const isArchived = Boolean(x.archived);
        return state.archivedFilter ? isArchived : !isArchived;
      });
    },
    getArchivedPods: function(state) {
      return this.getPods.filter(function(x) {
        const isArchived = Boolean(x.archived);
        return state.archivedFilter ? isArchived : !isArchived;
      });
    },


    // Combine all nodes and pos
    nodesAndPods: function() {
      // if we show the archived nodes and pods separately
      // return getters.getArchivedNodes.concat(getters.getArchivedPods);

      // but now we show them together
      return this.getNodes.concat(this.getPods);
    },


    // available node types
    // getAvailableNodeTypes: function(state, getters) {
    //   let types = getters.getArchivedNodes.map(x => x.type);
    //   types = types.concat(state.selectedNodeTypes);
    //   let uniqueTypes = types.filter((x, i, a) => a.indexOf(x) == i); // remove duplicated
    //   return uniqueTypes.sort();
    // },
    // // filter by node types
    // filterByNodeTypes: function(state, getters) {
    //   if (state.selectedNodeTypes.length == 0) {
    //     return getters.nodesAndPods;
    //   }

    //   return getters.nodesAndPods.filter(function(x) {
    //     return state.selectedNodeTypes.indexOf(x.type) >= 0;
    //   });
    // },
    filterByNodeTypes: function () {
      return this.nodesAndPods;
    },
    getAvailableDates: function() {
      if (this.filterByNodeTypes.length === 0) {
        return [];
      }

      let minDate = new Date(this.filterByNodeTypes[0].createdat);
      let maxDate = new Date(this.filterByNodeTypes[0].createdat);

      for (let i = 1; i < this.filterByNodeTypes.length; i++) {
        let createdAt = new Date(this.filterByNodeTypes[i].createdat);
        if (createdAt > maxDate)  maxDate = createdAt;
        if (createdAt < minDate)  minDate = createdAt;
      }

      return [minDate, maxDate];
    },
    // filter by created at dates range
    filterByDatesRange: function(state) {
      if (state.datesRange.length === 1) {
        return this.filterByNodeTypes.filter(function(x) {
          return formatDate(new Date(x.createdat)) === formatDate(state.datesRange[0]);
        });
      }

      if (state.datesRange.length > 1) {
        const minDate = min(state.datesRange);
        const maxDate = max(state.datesRange);

        return this.filterByNodeTypes.filter(function(x) {
          const dateCreated = new Date(x.createdat);
          return dateCreated >= minDate && dateCreated <= maxDate;
        });
      }

      return this.filterByNodeTypes;
    },



    // get available uploaders (array)
    getAvailableUploaders: function(state) {
      let uploaders = {};
      for (let item of this.filterByDatesRange) {
        if (!item.createdby)
          continue;
        uploaders[item.createdby.id] = item.createdby;
      }
      for (let upl of state.selectedUploaders) {
        uploaders[upl.id] = upl;
      }

      return Object.values(uploaders).map(upl => ({
        ...upl,
        name: `${upl.firstname} ${upl.lastname}`,
      }));
    },
    // filter by uploader
    filterByUploader: function(state) {
      if (state.selectedUploaders.length === 0) {
        return this.filterByDatesRange;
      }

      const uploaderIDs = state.selectedUploaders.map(x => x.id);
      return this.filterByDatesRange.filter(x => uploaderIDs.indexOf(x.createdby?.id) >= 0);
    },

    // get available file types
    getAvailableFileTypes: function(state) {
      let filetypes = [].concat(state.selectedFileTypes);

      for (let item of this.filterByUploader) {
        if (item && item.asset && item.asset.raw && item.asset.raw[0] && item.asset.raw[0].filetype) {
          filetypes.push(item.asset.raw[0].filetype);
        }
      }

      // return unique file types
      return filetypes.filter((value, index, self) => self.indexOf(value) === index);
    },
    // Filter by file types
    filterByFileTypes: function(state) {
      if (state.selectedFileTypes.length == 0) {
        return this.filterByUploader;
      }

      return this.filterByUploader.filter(function (item) {
        if (item && item.asset && item.asset.raw && item.asset.raw[0] && item.asset.raw[0].filetype) {
          let type = item.asset.raw[0].filetype;
          if (state.selectedFileTypes.indexOf(type) >= 0) {
            return true;
          }
        }

        return false;
      });
    },

    // Get available pod statuses
    getAvailablePodStatuses: function(state) {
      let statuses = [].concat(state.selectedPodStatuses);
      for (let item of this.filterByFileTypes) {
        if (item.badgestatus !== null) {
          statuses.push(item.badgestatus);
        }
      }

      return statuses.filter((value, index, self) => self.indexOf(value) === index).sort();
    },
    // Filter by pod statuses
    filterByPodStatus: function(state) {
      if (state.selectedPodStatuses.length == 0) {
        return this.filterByFileTypes;
      }

      return this.filterByFileTypes.filter(function(item) {
        if (
            item.badgestatus !== null &&
            state.selectedPodStatuses.indexOf(item.badgestatus) >= 0
          ) {
            return true;
        }

        return false;
      });
    },

    sortByColumn: function(state) {
      if (!state.sortColumn) return this.filterByPodStatus;

      const items = [...this.filterByPodStatus];
      switch (state.sortColumn) {
        case 'type': items.sort(sortByType); break;
        case 'name': items.sort(sortByName); break;
        case 'size': items.sort(sortBySize); break;
        case 'date': items.sort(sortByDate); break;
        case 'note': items.sort(sortByNote); break;
        default: break;
      }

      if (state.sortDesc) {
        return items.reverse();
      }
      return items;


      function sortByType (a, b) {
        if (a.nodeid && b.nodeid) return sortByName(a, b);
        if (a.nodeid && !b.nodeid) return 1;
        if (!a.nodeid && b.nodeid) return -1;
        if (!a.nodeid && !b.nodeid) return a.type - b.type;
      }
      function sortByName (a, b) {
        return a.name.localeCompare(b.name);
      }
      function sortBySize(a, b) {
        let sizeA = a.size || 0;
        let sizeB = b.size || 0;

        return sizeA - sizeB;
      }
      function sortByDate (a, b) {
        let dateA = new Date(a.createdat);
        let dateB = new Date(b.createdat);

        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;
        return 0;
      }
      function sortByNote (a, b) {
        if (a.note && b.note) return a.note.localeCompare(b.note);
        if (a.note && !b.note) return 1;
        if (!a.note && b.note) return -1;
        // if (!a.note && !b.note) return 0;
        return 0;
      }
    },


    // the final list
    getFilteredList: function() {
      return this.sortByColumn
        .filter(x => x.deletedat === null);
    },

    hasFiltersApplied: function() {
      return this.datesRange.length > 0
        || this.selectedUploaders.length > 0
        || this.selectedPodStatuses.length > 0
        || this.selectedFileTypes.length > 0
    },
  },
  actions: {
    // TODO: reset the selected nodes and pods when changing the filters
    setArchivedFilter: function(archived) {
      this.archivedFilter = Boolean(archived);
    },
    // setNodeTypeFilter: function(this, nodeTypeIds) {
    //   this.selectedNodeTypes = nodeTypeIds;
    // },
    setDatesRangeFilter: function(datesRange) {
      this.datesRange = datesRange;
    },
    setUploadersFilter: function(uploaders) {
      this.selectedUploaders = uploaders;
    },
    setFiletypesFilter: function(fileTypes) {
      this.selectedFileTypes = fileTypes;
    },
    setPodStatusFilter: function(podStatuses) {
      this.selectedPodStatuses = podStatuses;
    },
    setSortColumn: function(sortByColumnName) {
      if (sortByColumnName === null) {
        this.sortColumn = '';
        this.sortDesc = false;
        return;
      }

      if (this.sortColumn == sortByColumnName) {
        this.sortDesc = !this.sortDesc;
        if (!this.sortDesc) {
          this.sortColumn = '';
        }
      } else {
        this.sortColumn = sortByColumnName;
        this.sortDesc = false;
      }
    },
  },
});
